import React, { useState, useRef, useEffect } from 'react';
import { WorkspaceView, Store, AmazonConnection, CjConnection, AuthUser } from '../types';

interface TopbarProps {
  activeView: WorkspaceView;
  onSwitchView: (view: WorkspaceView, focusTab?: boolean) => void;
  stores: Store[];
  activeStoreId: string;
  onChangeStoreId: (storeId: string) => void;
  amazonConnection: AmazonConnection;
  cjConnection?: CjConnection;
  currentUser?: AuthUser | null;
  orderCount?: number;
  onLogout?: () => void;
  onOpenAmazonConnect: (triggerEl: HTMLElement | null) => void;
  onOpenNewStore: (triggerEl: HTMLElement | null) => void;
}

interface SubTabItem {
  id: string;
  view: WorkspaceView;
  num: string;
  label: string;
  shortDesc: string;
  panelId: string;
}

interface NavPillar {
  id: string;
  num: string;
  label: string;
  defaultView: WorkspaceView;
  items: SubTabItem[];
}

const NAV_PILLARS: NavPillar[] = [
  {
    id: 'pillar-catalog',
    num: '01',
    label: 'Catalog & Research',
    defaultView: 'products',
    items: [
      {
        id: 'tab-products',
        view: 'products',
        num: '1.1',
        label: 'Products Catalog',
        shortDesc: '206 Curated Products · Live Amazon SP-API & US 3PL',
        panelId: 'view-products'
      },
      {
        id: 'tab-research',
        view: 'research',
        num: '1.2',
        label: 'Research & Thesis',
        shortDesc: 'Market Demand Dossiers & Competitor Benchmarks',
        panelId: 'view-research'
      }
    ]
  },
  {
    id: 'pillar-storefronts',
    num: '02',
    label: 'Storefronts & Launch',
    defaultView: 'stores',
    items: [
      {
        id: 'tab-stores',
        view: 'stores',
        num: '2.1',
        label: 'My Stores',
        shortDesc: 'Connected Multi-Channel Storefronts & Portfolio',
        panelId: 'view-stores'
      },
      {
        id: 'tab-preview',
        view: 'preview',
        num: '2.2',
        label: 'Storefront Preview',
        shortDesc: 'Live Customer Buy-Box Preview (Amazon, Shopify, TikTok)',
        panelId: 'view-preview'
      },
      {
        id: 'tab-launch',
        view: 'launch',
        num: '2.3',
        label: 'Profit & Launch',
        shortDesc: '5-Channel Unit Economics & Inbound PO Dispatch',
        panelId: 'view-launch'
      }
    ]
  },
  {
    id: 'pillar-ads',
    num: '03',
    label: 'Amazon Ads',
    defaultView: 'ads',
    items: [
      {
        id: 'tab-ads',
        view: 'ads',
        num: '3.1',
        label: 'Amazon Ads & ROI',
        shortDesc: 'Live Campaign Analytics, ACOS & Sponsored Products v3',
        panelId: 'view-ads'
      }
    ]
  },
  {
    id: 'pillar-ops',
    num: '04',
    label: 'Orders & Operations',
    defaultView: 'orders',
    items: [
      {
        id: 'tab-orders',
        view: 'orders',
        num: '4.1',
        label: 'Orders & Fulfillment',
        shortDesc: 'Live Customer Orders, CJ US Tracking & Inline Editor',
        panelId: 'view-orders'
      },
      {
        id: 'tab-calendar',
        view: 'calendar',
        num: '4.2',
        label: 'Calendar & Goals',
        shortDesc: 'Launch Milestones, Inventory Schedule & Revenue Targets',
        panelId: 'view-calendar'
      },
      {
        id: 'tab-reports',
        view: 'reports',
        num: '4.3',
        label: 'Email Reports',
        shortDesc: 'Automated Executive Performance Digests & Alerts',
        panelId: 'view-reports'
      }
    ]
  }
];

const ALL_TABS: SubTabItem[] = NAV_PILLARS.flatMap((p) => p.items);

export const Topbar: React.FC<TopbarProps> = ({
  activeView,
  onSwitchView,
  stores,
  activeStoreId,
  onChangeStoreId,
  amazonConnection,
  cjConnection,
  currentUser,
  orderCount = 0,
  onLogout,
  onOpenAmazonConnect,
  onOpenNewStore
}) => {
  const [openPillarId, setOpenPillarId] = useState<string | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const hoverTimeoutRef = useRef<number | null>(null);

  const activePillar =
    NAV_PILLARS.find((pillar) => pillar.items.some((item) => item.view === activeView)) || NAV_PILLARS[0];
  const activeSubItem = activePillar.items.find((item) => item.view === activeView) || activePillar.items[0];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenPillarId(null);
      }
    };
    const handleKeyOutside = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenPillarId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyOutside);
    };
  }, []);

  const handleMouseEnter = (pillarId: string) => {
    if (hoverTimeoutRef.current) window.clearTimeout(hoverTimeoutRef.current);
    setOpenPillarId(pillarId);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) window.clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = window.setTimeout(() => {
      setOpenPillarId(null);
    }, 150);
  };

  return (
    <header className="topbar">
      {/* TIER 1: EXECUTIVE TELEMETRY & ACCOUNT UTILITY STRIP */}
      <div className="topbar-utility-bar">
        <div className="container topbar-utility-inner">
          <div className="topbar-telemetry-group">
            <span className="utility-status-pill" id="firebase-cloud-badge" title="Synced with Firebase Cloud">
              <span className="status-dot" aria-hidden="true"></span>
              <span>Cloud Synced</span>
            </span>

            <span className="utility-divider" aria-hidden="true"></span>

            <button
              type="button"
              id="open-amazon-connect-btn"
              className={`utility-api-btn ${amazonConnection?.connected ? 'connected' : ''}`}
              onClick={(e) => onOpenAmazonConnect(e.currentTarget)}
              title={
                amazonConnection?.connected
                  ? `Connected to Amazon Seller Central · Merchant ID: ${amazonConnection.sellerId} (Click to configure)`
                  : 'Connect your Amazon Seller Central account'
              }
            >
              <span className="status-dot" aria-hidden="true"></span>
              <span id="amazon-connect-label" className="utility-api-text">
                {amazonConnection?.connected ? (
                  <>
                    <span>Amazon SP-API</span>
                    <code className="utility-code tabular">{amazonConnection.sellerId}</code>
                  </>
                ) : (
                  <span>Connect Amazon</span>
                )}
              </span>
            </button>

            <span className="utility-divider" aria-hidden="true"></span>

            <button
              type="button"
              id="open-cj-connect-btn"
              className={`utility-api-btn ${cjConnection?.connected ? 'connected' : ''}`}
              onClick={(e) => onOpenAmazonConnect(e.currentTarget)}
              title={
                cjConnection?.connected
                  ? `CJ Dropshipping API v2.0 Live · User: CJ5963658 · OpenID: ${cjConnection.openId} · 50,000 pts (Click to view)`
                  : 'Connect CJ Dropshipping API'
              }
            >
              <span className="status-dot" aria-hidden="true"></span>
              <span className="utility-api-text">
                <span>CJ US-Warehouse</span>
                <code className="utility-code tabular">{cjConnection?.connected ? 'Live' : 'Connect'}</code>
              </span>
            </button>
          </div>

          {currentUser && (
            <div className="topbar-account-group">
              <span className="account-role-badge">ADMIN</span>
              <span className="account-email-text">{currentUser.email}</span>
              {onLogout && (
                <button
                  type="button"
                  id="topbar-logout-btn"
                  className="btn-utility-signout"
                  onClick={onLogout}
                  title={`Sign out (${currentUser.email})`}
                >
                  Sign Out
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* TIER 2: PRIMARY BRAND, 4-PILLAR CONCIERGE NAVIGATION & STOREFRONT CONTROLS */}
      <div className="topbar-main-bar">
        <div className="container topbar-inner">
          <div className="brand-lockup">
            <div className="brand-identity">
              <span className="brand-mark">AURELIUS</span>
              <span className="brand-sub">COMMERCE HUB</span>
            </div>
          </div>

          <nav
            ref={navRef}
            className="workspace-tabs workspace-pillars"
            aria-label="Workspace Pillars"
            onMouseLeave={handleMouseLeave}
          >
            {NAV_PILLARS.map((pillar) => {
              const isPillarActive = pillar.items.some((item) => item.view === activeView);
              const hasSubmenu = pillar.items.length > 1;
              const isDropdownOpen = openPillarId === pillar.id && hasSubmenu;
              const activeChild = pillar.items.find((item) => item.view === activeView);

              return (
                <div
                  key={pillar.id}
                  className={`pillar-dropdown-wrap ${isDropdownOpen ? 'is-open' : ''}`}
                  onMouseEnter={() => hasSubmenu && handleMouseEnter(pillar.id)}
                >
                  <button
                    type="button"
                    id={hasSubmenu ? pillar.id : pillar.items[0].id}
                    className={`tab-btn pillar-btn ${isPillarActive ? 'active' : ''}`}
                    aria-expanded={hasSubmenu ? isDropdownOpen : undefined}
                    aria-haspopup={hasSubmenu ? 'menu' : undefined}
                    onClick={() => {
                      if (!isPillarActive) {
                        onSwitchView(pillar.defaultView, false);
                      }
                      if (hasSubmenu) {
                        setOpenPillarId((prev) => (prev === pillar.id ? null : pillar.id));
                      } else {
                        setOpenPillarId(null);
                      }
                    }}
                  >
                    <span className="tab-num tabular">{pillar.num}</span>
                    <span className="tab-label">{pillar.label}</span>
                    {pillar.id === 'pillar-storefronts' && (
                      <span id="nav-store-count" className="tab-count-badge tabular">
                        {stores.length}
                      </span>
                    )}
                    {pillar.id === 'pillar-ops' && orderCount > 0 && (
                      <span id="nav-order-count" className="tab-count-badge tabular">
                        {orderCount}
                      </span>
                    )}
                    {hasSubmenu && (
                      <span className={`pillar-chevron ${isDropdownOpen ? 'open' : ''}`} aria-hidden="true">
                        ▾
                      </span>
                    )}
                  </button>

                  {hasSubmenu && isDropdownOpen && (
                    <div className="pillar-submenu-popover" role="menu" aria-label={`${pillar.label} submenu`}>
                      <div className="pillar-submenu-header">
                        <span className="pillar-submenu-kicker tabular">{pillar.num} · {pillar.label.toUpperCase()}</span>
                        {activeChild && <span className="pillar-submenu-active-tag">Active: {activeChild.label}</span>}
                      </div>
                      <div className="pillar-submenu-list">
                        {pillar.items.map((sub) => {
                          const isSubActive = activeView === sub.view;
                          return (
                            <button
                              key={sub.id}
                              type="button"
                              role="menuitem"
                              className={`pillar-submenu-item ${isSubActive ? 'is-active' : ''}`}
                              data-view={sub.view}
                              onClick={() => {
                                onSwitchView(sub.view, false);
                                setOpenPillarId(null);
                              }}
                            >
                              <div className="pillar-submenu-item-top">
                                <span className="pillar-submenu-num tabular">{sub.num}</span>
                                <span className="pillar-submenu-title">{sub.label}</span>
                                {sub.view === 'stores' && (
                                  <span className="pillar-submenu-badge tabular">{stores.length} stores</span>
                                )}
                                {sub.view === 'orders' && orderCount > 0 && (
                                  <span className="pillar-submenu-badge tabular">{orderCount} orders</span>
                                )}
                                {isSubActive && <span className="pillar-submenu-dot" aria-hidden="true">●</span>}
                              </div>
                              <span className="pillar-submenu-desc">{sub.shortDesc}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          <div className="topbar-actions">
            <label htmlFor="global-store-switcher" className="sr-only">
              Active Storefront
            </label>
            <select
              id="global-store-switcher"
              className="store-select tabular"
              value={activeStoreId}
              onChange={(e) => onChangeStoreId(e.target.value)}
              title="Switch active storefront context"
            >
              <option value="all">All Stores ({stores.length})</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} · {s.channel}
                </option>
              ))}
            </select>

            <button
              type="button"
              id="open-new-store-modal-btn"
              className="btn-topbar-new"
              onClick={(e) => onOpenNewStore(e.currentTarget)}
            >
              <span aria-hidden="true">+</span>
              <span>New Store</span>
            </button>
          </div>
        </div>
      </div>

      {/* TIER 3: CONTEXTUAL SUB-TAB STRIP FOR FAST 1-CLICK SUBMENU SWITCHING */}
      <div className="topbar-subnav-bar">
        <div className="container topbar-subnav-inner">
          <div className="subnav-breadcrumb">
            <span className="subnav-pillar-num tabular">{activePillar.num}</span>
            <span className="subnav-pillar-name">{activePillar.label}</span>
            <span className="subnav-sep" aria-hidden="true">›</span>
            <strong className="subnav-active-name">{activeSubItem.label}</strong>
            <span className="subnav-active-desc">{activeSubItem.shortDesc}</span>
          </div>

          <div className="subnav-pills" role="tablist" aria-label={`${activePillar.label} Views`}>
            {activePillar.items.map((item) => {
              const isItemActive = activeView === item.view;
              return (
                <button
                  key={item.id}
                  type="button"
                  id={item.id}
                  role="tab"
                  aria-selected={isItemActive ? 'true' : 'false'}
                  aria-controls={item.panelId}
                  data-view={item.view}
                  className={`subnav-pill-btn ${isItemActive ? 'active' : ''}`}
                  onClick={() => onSwitchView(item.view, false)}
                >
                  <span className="subnav-pill-num tabular">{item.num}</span>
                  <span>{item.label}</span>
                </button>
              );
            })}
            {/* Hidden quick-access buttons for non-active pillar tabs so any #tab-* DOM selector continues to work */}
            <div hidden aria-hidden="true">
              {ALL_TABS.filter((t) => !activePillar.items.some((i) => i.id === t.id)).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  id={t.id}
                  data-view={t.view}
                  onClick={() => onSwitchView(t.view, false)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
